import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core'
import { ActivatedRoute, ParamMap, Params, Router } from '@angular/router'

import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'

import {
  TRYGROUP_SEGMENTS,
  TrygroupDashboardPriorityListService,
  TrygroupPriorityListDepartment,
  TrygroupPriorityListFilter,
  TrygroupPriorityListItem,
  TrygroupPriorityListPageInfo,
  TrygroupPriorityListUserCategory,
  TrygroupSegment,
  createDefaultPriorityListFilter,
  formatSnapshotDate,
  getComparisonSnapshotDate,
  getDefaultSnapshotDate,
} from '../../../services/trygroup-dashboard/trygroup-dashboard-priority-list.service'
import { PriorityListFilterPaneComponent } from './priority-list-filter-pane.component'
import { PriorityListHeaderComponent } from './priority-list-header.component'
import { PriorityListPaginationComponent } from './priority-list-pagination.component'
import { PriorityListTableComponent } from './priority-list-table.component'

@UntilDestroy()
@Component({
  selector: 'app-trygroup-priority-list-page',
  standalone: true,
  imports: [
    PriorityListHeaderComponent,
    PriorityListFilterPaneComponent,
    PriorityListTableComponent,
    PriorityListPaginationComponent,
  ],
  template: `
    <section class="priority-list-page">
      <app-priority-list-header
        [snapshotDate]="snapshotDate"
        [comparisonSnapshotDate]="comparisonSnapshotDate"
        [disabled]="isLoading || isLoadingMore"
        (snapshotDateChange)="onSnapshotDateChange($event)"
        (comparisonSnapshotDateChange)="onComparisonSnapshotDateChange($event)"
      ></app-priority-list-header>

      <app-priority-list-filter-pane
        [filter]="filter"
        [userCategories]="userCategories"
        [departments]="departments"
        [disabled]="isLoading || isLoadingMore"
        (filterChange)="onFilterChange($event)"
      ></app-priority-list-filter-pane>

      @if (errorMessage) {
        <div class="error" role="alert" aria-live="assertive">
          <span>{{ errorMessage }}</span>
          <button
            type="button"
            class="retry-button"
            [disabled]="isLoading || isLoadingMore"
            (click)="retry()"
          >
            再試行
          </button>
        </div>
      }

      <app-priority-list-table
        [items]="items"
        [isLoading]="isLoading"
      ></app-priority-list-table>

      <app-priority-list-pagination
        [hasNextPage]="pageInfo.hasNextPage"
        [isLoading]="isLoadingMore"
        [loadedCount]="items.length"
        [totalCount]="totalCount"
        (loadMore)="loadMore()"
      ></app-priority-list-pagination>
    </section>
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [
    `
      .priority-list-page {
        min-width: 0;
      }

      .error {
        display: flex;
        gap: 12px;
        align-items: center;
        justify-content: space-between;
        padding: 12px 14px;
        margin: 0 0 16px;
        color: #b91c1c;
        font-size: 13px;
        font-weight: 700;
        background: #fef2f2;
        border: 1px solid #fecaca;
        border-radius: 8px;
      }

      .retry-button {
        flex: 0 0 auto;
        padding: 6px 12px;
        color: #ffffff;
        font-size: 13px;
        font-weight: 700;
        cursor: pointer;
        background: #b91c1c;
        border: 0;
        border-radius: 6px;
      }

      .retry-button:disabled {
        cursor: not-allowed;
        opacity: 0.6;
      }
    `,
  ],
})
export class TrygroupPriorityListPageComponent implements OnInit {
  private readonly defaultSnapshotDate = formatSnapshotDate(
    getDefaultSnapshotDate(),
  )
  // 日次バッチが翌朝集計するため、表示日デフォルトは「昨日」とする
  snapshotDate = this.defaultSnapshotDate
  comparisonSnapshotDate = formatSnapshotDate(
    getComparisonSnapshotDate(parseSnapshotDate(this.snapshotDate)),
  )
  /** 比較元をユーザーが手動指定したか。true なら表示日変更時に自動再計算しない。 */
  private isComparisonSnapshotDateManual = false
  filter = createDefaultPriorityListFilter()
  userCategories: TrygroupPriorityListUserCategory[] = []
  departments: TrygroupPriorityListDepartment[] = []
  items: TrygroupPriorityListItem[] = []
  pageInfo: TrygroupPriorityListPageInfo = {
    hasNextPage: false,
    endCursor: null,
  }
  totalCount = 0
  isLoading = true
  isLoadingMore = false
  errorMessage = ''
  private lastQueryParams: Params = {}

  constructor(
    private trygroupDashboardPriorityListService: TrygroupDashboardPriorityListService,
    private route: ActivatedRoute,
    private router: Router,
  ) {}

  ngOnInit(): void {
    this.route.queryParamMap
      .pipe(untilDestroyed(this))
      .subscribe((queryParamMap) => {
        const state = parsePriorityListQueryParamMap(
          queryParamMap,
          this.defaultSnapshotDate,
        )
        this.applyUrlState(state)
        this.lastQueryParams = buildPriorityListQueryParams(
          state,
          this.defaultSnapshotDate,
        )
        this.load({ append: false })
      })
  }

  onSnapshotDateChange(snapshotDate: string): void {
    if (!isValidSnapshotDateInput(snapshotDate)) {
      return
    }

    this.navigateToState({
      snapshotDate,
      comparisonSnapshotDate: this.isComparisonSnapshotDateManual
        ? this.comparisonSnapshotDate
        : formatSnapshotDate(
            getComparisonSnapshotDate(parseSnapshotDate(snapshotDate)),
          ),
      isComparisonSnapshotDateManual: this.isComparisonSnapshotDateManual,
      filter: this.filter,
    })
  }

  onComparisonSnapshotDateChange(comparisonSnapshotDate: string): void {
    if (!isValidSnapshotDateInput(comparisonSnapshotDate)) {
      return
    }

    this.navigateToState({
      snapshotDate: this.snapshotDate,
      comparisonSnapshotDate,
      isComparisonSnapshotDateManual: true,
      filter: this.filter,
    })
  }

  onFilterChange(filter: TrygroupPriorityListFilter): void {
    this.navigateToState({
      snapshotDate: this.snapshotDate,
      comparisonSnapshotDate: this.comparisonSnapshotDate,
      isComparisonSnapshotDateManual: this.isComparisonSnapshotDateManual,
      filter,
    })
  }

  loadMore(): void {
    if (!this.pageInfo.hasNextPage || this.pageInfo.endCursor == null) {
      return
    }

    this.load({ append: true })
  }

  retry(): void {
    this.load({ append: false })
  }

  private load(options: { append: boolean }): void {
    this.errorMessage = ''
    if (options.append) {
      this.isLoadingMore = true
    } else {
      this.isLoading = true
      this.items = []
      this.pageInfo = {
        hasNextPage: false,
        endCursor: null,
      }
    }

    this.trygroupDashboardPriorityListService
      .getPriorityList({
        snapshotDate: parseSnapshotDate(this.snapshotDate),
        comparisonSnapshotDate: this.isComparisonSnapshotDateManual
          ? parseSnapshotDate(this.comparisonSnapshotDate)
          : undefined,
        filter: this.filter,
        cursor: options.append ? this.pageInfo.endCursor : null,
      })
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (page) => {
          this.userCategories = page.userCategories
          this.departments = page.departments
          if (this.hasUnknownSelectedDepartment(page.departments)) {
            this.navigateToState({
              snapshotDate: this.snapshotDate,
              comparisonSnapshotDate: this.comparisonSnapshotDate,
              isComparisonSnapshotDateManual:
                this.isComparisonSnapshotDateManual,
              filter: {
                ...this.filter,
                departmentId: null,
              },
            })
            return
          }

          this.items = options.append
            ? [...this.items, ...page.items]
            : page.items
          this.totalCount = page.totalCount
          this.pageInfo = page.pageInfo
          // 手動指定時はユーザーが選んだ日付を尊重し、レスポンスでの上書きをスキップ
          if (!this.isComparisonSnapshotDateManual) {
            this.comparisonSnapshotDate = page.comparisonSnapshotDate
          }
          this.isLoading = false
          this.isLoadingMore = false
        },
        error: () => {
          this.isLoading = false
          this.isLoadingMore = false
          // TODO(follow-up): throwIfCustomError 経由で受け取った CustomError の
          // extensions.code (FORBIDDEN / VALIDATION_ERROR 等) でメッセージを分岐し、
          // ユーザーがセルフリカバリーしやすくする (AccessGrant 失効 → 「アクセス権限が
          // ありません」、検索 100 文字超え → 「検索キーワードは 100 文字以内です」等)。
          this.errorMessage = '育成優先リストの取得に失敗しました。'
        },
      })
  }

  private navigateToState(state: PriorityListUrlState): void {
    const queryParams = buildPriorityListQueryParams(
      state,
      this.defaultSnapshotDate,
    )

    if (areQueryParamsEqual(queryParams, this.lastQueryParams)) {
      this.applyUrlState(state)
      this.load({ append: false })
      return
    }

    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams,
    })
  }

  private applyUrlState(state: PriorityListUrlState): void {
    this.snapshotDate = state.snapshotDate
    this.comparisonSnapshotDate = state.comparisonSnapshotDate
    this.isComparisonSnapshotDateManual = state.isComparisonSnapshotDateManual
    this.filter = state.filter
  }

  private hasUnknownSelectedDepartment(
    departments: TrygroupPriorityListDepartment[],
  ): boolean {
    const departmentId = this.filter.departmentId
    if (departmentId == null) {
      return false
    }

    return !departments.some((department) => department.id === departmentId)
  }
}

export type PriorityListUrlState = {
  snapshotDate: string
  comparisonSnapshotDate: string
  isComparisonSnapshotDateManual: boolean
  filter: TrygroupPriorityListFilter
}

const QUERY_PARAM = {
  snapshotDate: 'snapshotDate',
  comparisonSnapshotDate: 'comparisonSnapshotDate',
  segment: 'segment',
  userCategoryId: 'userCategoryId',
  requiresGuidanceOnly: 'requiresGuidanceOnly',
  departmentId: 'departmentId',
  searchKeyword: 'searchKeyword',
} as const

export function parsePriorityListQueryParamMap(
  queryParamMap: ParamMap,
  defaultSnapshotDate: string,
): PriorityListUrlState {
  const rawSnapshotDate = queryParamMap.get(QUERY_PARAM.snapshotDate)
  const snapshotDate =
    rawSnapshotDate != null && isValidSnapshotDateInput(rawSnapshotDate)
      ? rawSnapshotDate
      : defaultSnapshotDate

  const autoComparisonSnapshotDate = formatSnapshotDate(
    getComparisonSnapshotDate(parseSnapshotDate(snapshotDate)),
  )
  const rawComparisonSnapshotDate = queryParamMap.get(
    QUERY_PARAM.comparisonSnapshotDate,
  )
  let isComparisonSnapshotDateManual = false
  let comparisonSnapshotDate = autoComparisonSnapshotDate
  if (
    rawComparisonSnapshotDate != null &&
    isValidSnapshotDateInput(rawComparisonSnapshotDate)
  ) {
    isComparisonSnapshotDateManual = true
    comparisonSnapshotDate = rawComparisonSnapshotDate
  }
  const searchKeyword = normalizeNullableQueryValue(
    queryParamMap.get(QUERY_PARAM.searchKeyword),
  )

  return {
    snapshotDate,
    comparisonSnapshotDate,
    isComparisonSnapshotDateManual,
    filter: {
      segments: uniqueValues(
        queryParamMap.getAll(QUERY_PARAM.segment).filter(isTrygroupSegment),
      ),
      userCategoryIds: uniqueValues(
        queryParamMap
          .getAll(QUERY_PARAM.userCategoryId)
          .map((value) => value.trim())
          .filter((value) => value.length > 0),
      ),
      requiresGuidanceOnly:
        queryParamMap.get(QUERY_PARAM.requiresGuidanceOnly) === 'true',
      departmentId: normalizeNullableQueryValue(
        queryParamMap.get(QUERY_PARAM.departmentId),
      ),
      searchKeyword: searchKeyword == null ? '' : searchKeyword,
    },
  }
}

export function buildPriorityListQueryParams(
  state: PriorityListUrlState,
  defaultSnapshotDate: string,
): Params {
  const queryParams: Params = {}

  if (state.snapshotDate !== defaultSnapshotDate) {
    queryParams[QUERY_PARAM.snapshotDate] = state.snapshotDate
  }

  if (state.isComparisonSnapshotDateManual) {
    queryParams[QUERY_PARAM.comparisonSnapshotDate] =
      state.comparisonSnapshotDate
  }

  if (state.filter.segments.length > 0) {
    queryParams[QUERY_PARAM.segment] = state.filter.segments
  }

  if (state.filter.userCategoryIds.length > 0) {
    queryParams[QUERY_PARAM.userCategoryId] = state.filter.userCategoryIds
  }

  if (state.filter.requiresGuidanceOnly) {
    queryParams[QUERY_PARAM.requiresGuidanceOnly] = 'true'
  }

  if (state.filter.departmentId != null && state.filter.departmentId !== '') {
    queryParams[QUERY_PARAM.departmentId] = state.filter.departmentId
  }

  const searchKeyword = state.filter.searchKeyword.trim()
  if (searchKeyword.length > 0) {
    queryParams[QUERY_PARAM.searchKeyword] = searchKeyword
  }

  return queryParams
}

function areQueryParamsEqual(a: Params, b: Params): boolean {
  return (
    JSON.stringify(normalizeQueryParams(a)) ===
    JSON.stringify(normalizeQueryParams(b))
  )
}

function normalizeQueryParams(params: Params): Record<string, string[]> {
  return Object.keys(params)
    .sort()
    .reduce<Record<string, string[]>>((normalized, key) => {
      const value = params[key]
      const values = Array.isArray(value) ? value : [value]
      normalized[key] = values.map((item) => String(item))
      return normalized
    }, {})
}

function uniqueValues<T extends string>(values: T[]): T[] {
  return values.filter((value, index) => values.indexOf(value) === index)
}

function normalizeNullableQueryValue(value: string | null): string | null {
  if (value == null) {
    return null
  }

  const trimmedValue = value.trim()
  return trimmedValue.length > 0 ? trimmedValue : null
}

function isTrygroupSegment(value: string): value is TrygroupSegment {
  return TRYGROUP_SEGMENTS.includes(value as TrygroupSegment)
}

// TODO(follow-up): ローカル TZ 依存。CI の TZ を Asia/Tokyo に明示しておかないと
// 別 TZ 環境で formatSnapshotDate との往復で日付ズレが発生する可能性あり。
function parseSnapshotDate(snapshotDate: string): Date {
  const [year, month, day] = snapshotDate.split('-').map(Number)
  return new Date(year, month - 1, day)
}

function isValidSnapshotDateInput(snapshotDate: string): boolean {
  return /^\d{4}-\d{2}-\d{2}$/.test(snapshotDate)
}
